import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useApolloClient, useMutation } from '@apollo/client';
import { CheckboxInput, Dialog, DialogActions, DialogContent, RadioButtons, TextArea, TextInput, Typography, } from '@guildeducationinc/recess';
import { AccreditationsPicker } from '@src/components/Admin/AccreditationsPicker/AccreditationsPicker';
import { UPDATE_APPROVAL_TASK } from '@src/graphql/queries/approvals';
import { PAYMENT_REQUEST } from '@src/graphql/queries/paymentRequest';
import { PROGRAMS } from '@src/graphql/queries/program';
import { useAccreditations } from '@src/hooks/useAccreditations';
import { useCurrentCurrency } from '@src/hooks/useCurrentCurrency';
import { WorkflowRunTaskStatus, WorkflowType } from '@src/types';
import { isEmpty } from 'lodash';
import { COMMENTS, Confirm, Messages, MessageType, TrackableButton, useAlert, useLocale, } from 'pe-core-frontend';
import { useCallback, useMemo, useState } from 'react';
const getTitle = (t, action) => {
    switch (action) {
        case 'comment':
            return t('Add comment');
        case 'info':
            return t('Request info');
        case 'reject':
            return t('Reject');
        case 'approve':
            return t('Approve');
        case 'adjust_amount':
            return t('Adjust amount');
    }
};
const getCtaText = (t, action) => {
    switch (action) {
        case 'comment':
            return t('Add comment');
        case 'info':
            return t('Send request');
        case 'reject':
            return t('Reject');
        case 'approve':
            return t('Approve');
        case 'adjust_amount':
            return t('Approve adjusted amount');
    }
};
const getDescription = (t, action, approval, formatCurrency, currency) => {
    const isProgram = approval.workflowType === WorkflowType.PROGRAM_APPROVAL ||
        approval.workflowType === WorkflowType.OTHER_SCHOOL_APPROVAL;
    if (isProgram) {
        switch (action) {
            case 'comment':
                return null;
            case 'info':
                return t('Add a comment to the request explaining what additional information is needed from {employeeName}.', { values: { employeeName: approval.employeeName } });
            case 'reject':
                return t("Confirm the denial of {employeeName}'s application to {programName}.", {
                    values: { employeeName: approval.employeeName, programName: approval.programName },
                });
            case 'approve':
                return t("Confirm the approval of {employeeName}'s program application request. Optionally, add a note to the request.", { values: { employeeName: approval.employeeName } });
            case 'adjust_amount':
                return null;
        }
    }
    else {
        switch (action) {
            case 'comment':
                return null;
            case 'info':
                return t('Add a comment to the request explaining what additional information is needed from {employeeName}.', { values: { employeeName: approval.employeeName } });
            case 'reject':
                return t("Confirm the denial of {employeeName}'s reimbursement request. Add a comment to the request explaining the reasons for the denial.", { values: { employeeName: approval.employeeName } });
            case 'approve':
                return t("Confirm the approval of {employeeName}'s reimbursement request. Optionally, add a note to the request.", { values: { employeeName: approval.employeeName } });
            case 'adjust_amount':
                return t('You are adjusting the requested amount of {amount} for {employeeName}', {
                    values: {
                        employeeName: approval.employeeName,
                        amount: formatCurrency(approval.amountRequested || 0, currency, true),
                    },
                });
        }
    }
};
// Dev Note:
// value: '' | false | true | 'add' controls comment visbility.
// true means internal. false means public. add hits another logic condition section below, which defines false or true
const getCommentOptions = (t, action, approval) => {
    switch (action) {
        case 'comment':
            return [
                {
                    label: t('Public (Visible to {employeeName})', {
                        values: { employeeName: approval.employeeName },
                    }),
                    value: 'false',
                },
                {
                    label: t('Internal (Visible to approvers & admins)'),
                    value: 'true',
                },
            ];
        case 'info':
            return [
                {
                    label: t('Public (Visible to {employeeName})', {
                        values: { employeeName: approval.employeeName },
                    }),
                    value: 'false',
                },
            ];
        case 'reject':
            return [
                {
                    label: t('Public - Reason will be shared with {employeeName}', {
                        values: { employeeName: approval.employeeName },
                    }),
                    value: 'false',
                },
            ];
        case 'approve':
            return [
                {
                    label: t('No comment'),
                    value: '',
                },
                {
                    label: t('Add comment'),
                    value: 'add',
                },
            ];
        case 'adjust_amount':
            return [
                {
                    label: t('Public - Reason will be shared with {employeeName}', {
                        values: { employeeName: approval.employeeName },
                    }),
                    value: 'false',
                },
            ];
    }
};
const getSuccessTitle = (t, action) => {
    switch (action) {
        case 'info':
            return t('Request for additional information complete');
        case 'reject':
            return t('Denial complete');
        case 'approve':
            return t('Approval complete');
        case 'adjust_amount':
            return t('Approval with adjustment complete');
        default:
            return '';
    }
};
const getSuccessMessage = (t, action, employeeName) => {
    switch (action) {
        case 'info':
            return t('An email has been sent to {employeeName}.', { values: { employeeName } });
        case 'reject':
            return (_jsxs(_Fragment, { children: [t("You've"), " ", _jsx("b", { children: t('denied') }), ' ', t("{employeeName}'s request.", { values: { employeeName } })] }));
        case 'approve':
            return (_jsxs(_Fragment, { children: [t("You've"), " ", _jsx("b", { children: t('approved') }), ' ', t("{employeeName}'s request.", { values: { employeeName } })] }));
        case 'adjust_amount':
            return (_jsxs(_Fragment, { children: [t("You've"), " ", _jsx("b", { children: t('approved with adjustment') }), ' ', t("{employeeName}'s request.", { values: { employeeName } })] }));
        default:
            return '';
    }
};
export const CommentModal = ({ approval, action, onClose, onComplete, isModal = true, }) => {
    const { t, formatCurrency } = useLocale();
    const { showAlert } = useAlert();
    const currency = useCurrentCurrency({ clientId: approval.clientId });
    const { accreditations } = useAccreditations({ clientId: approval.clientId });
    const title = getTitle(t, action);
    const description = getDescription(t, action, approval, formatCurrency, currency);
    const commentOptions = getCommentOptions(t, action, approval);
    const [notes, setNotes] = useState('');
    const [commentOption, setCommentOption] = useState(commentOptions[0].value);
    const [displayCommentVisibility, setDisplayCommentVisibility] = useState('false');
    const [saving, setSaving] = useState(false);
    const [messages, setMessages] = useState([]);
    const [showConfirm, setShowConfirm] = useState(false);
    const [addToEmployerSchoolList, setAddToEmployerSchoolList] = useState(false);
    const [newApprovedAmount, setNewApprovedAmount] = useState(approval.amountRequested);
    const [skipGroupAssignment, setSkipGroupAssignment] = useState(false);
    const [selectedAccreditationIds, setSelectedAccreditationIds] = useState([]);
    // const [showGroupAssignmentInfo, setShowGroupAssignmentInfo] = useState(false);
    const hasAccreditations = accreditations.length > 0;
    const apolloClient = useApolloClient();
    const refetchQueries = useCallback(async () => {
        // Refetch all Approvals queries regardless of variables (this updates the header badge count)
        await apolloClient.refetchQueries({ include: ['Approvals'] });
        // Refetch related queries
        const queries = [
            {
                query: COMMENTS,
                variables: {
                    spec: {
                        commentableId: approval.workflowableId,
                        commentableType: approval.workflowType === WorkflowType.PROGRAM_APPROVAL ||
                            approval.workflowType === WorkflowType.OTHER_SCHOOL_APPROVAL
                            ? 'programs'
                            : 'payment_requests',
                    },
                },
            },
        ];
        if (approval.workflowType === WorkflowType.PROGRAM_APPROVAL ||
            approval.workflowType === WorkflowType.OTHER_SCHOOL_APPROVAL) {
            queries.push({
                query: PROGRAMS,
                variables: {
                    spec: { employeeId: approval.employeeId },
                },
            });
        }
        else {
            queries.push({
                query: PAYMENT_REQUEST,
                variables: {
                    id: approval.workflowableId,
                },
            });
        }
        // Refetch related queries
        await Promise.all(queries.map((q) => apolloClient.query({ ...q, fetchPolicy: 'network-only' })));
    }, [apolloClient, approval]);
    const [updateApprovalTask] = useMutation(UPDATE_APPROVAL_TASK);
    const [errors, setErrors] = useState({});
    const handleValidate = () => {
        const errors = {};
        if (!notes && action !== 'approve') {
            errors.commentText = t('required');
        }
        if (addToEmployerSchoolList &&
            hasAccreditations &&
            !skipGroupAssignment &&
            selectedAccreditationIds.length === 0) {
            errors.accreditations = t('Please select at least one group for the school');
        }
        if (action === 'adjust_amount') {
            if (newApprovedAmount === undefined || isNaN(newApprovedAmount)) {
                errors.newApprovedAmount = t('required');
            }
            else if (newApprovedAmount < 0) {
                errors.newApprovedAmount = t('cannot be less than 0');
            }
            else if (newApprovedAmount > (approval.amountRequested || 0)) {
                errors.newApprovedAmount = t('cannot be greater than the requested amount');
            }
        }
        setErrors(errors);
        return isEmpty(errors);
    };
    const handleSubmit = useCallback(async () => {
        setMessages([]);
        if (!handleValidate()) {
            return;
        }
        let status;
        switch (action) {
            case 'comment':
                status = WorkflowRunTaskStatus.IN_PROGRESS;
                break;
            case 'info':
                status = WorkflowRunTaskStatus.INFO_REQUESTED;
                break;
            case 'reject':
                status = WorkflowRunTaskStatus.REJECTED;
                break;
            case 'approve':
            case 'adjust_amount':
                status = WorkflowRunTaskStatus.APPROVED;
                break;
        }
        setSaving(true);
        try {
            const input = {
                workflowRunTaskId: approval.id,
                status,
                notes: commentOption === '' ? undefined : notes, // clear notes when No comment is selected
                isInternalComment: commentOption === 'add' ? displayCommentVisibility === 'true' : commentOption === 'true',
                extraData: {
                    addToEmployerSchoolList,
                    accreditationIds: selectedAccreditationIds,
                    ...(action === 'adjust_amount' && { newApprovedAmount }),
                },
            };
            const result = await updateApprovalTask({ variables: { input } });
            if (!result.data?.updateApprovalTask?.success) {
                setMessages([{ type: MessageType.ERROR, value: result.data.updateApprovalTask.message }]);
            }
            else {
                if (action === 'comment') {
                    showAlert(t('Request Updated'), 'success');
                    await refetchQueries();
                    onClose();
                }
                else {
                    setShowConfirm(true);
                }
            }
        }
        catch (e) {
            setMessages([{ type: MessageType.ERROR, value: t('Error saving approval') }]);
        }
        finally {
            setSaving(false);
        }
    }, [
        action,
        addToEmployerSchoolList,
        selectedAccreditationIds,
        approval.id,
        commentOption,
        displayCommentVisibility,
        newApprovedAmount,
        notes,
        onClose,
        updateApprovalTask,
        handleValidate,
        showAlert,
        refetchQueries,
        t,
    ]);
    const bodyContent = useMemo(() => (_jsxs(_Fragment, { children: [description && _jsx(Typography, { variant: 'body2', children: description }), _jsx(Messages, { messages: messages }), action === 'approve' && approval.workflowType === WorkflowType.OTHER_SCHOOL_APPROVAL && (_jsxs("div", { className: 'mb-5', children: [_jsx(CheckboxInput, { checked: addToEmployerSchoolList, label: t('school_add_employer'), onChange: e => setAddToEmployerSchoolList(e.currentTarget.checked), additionalInfo: t('school_add_employer_desc') }), addToEmployerSchoolList && hasAccreditations && (_jsxs("div", { className: 'mt-4', children: [_jsx("div", { className: 'flex items-center gap-2 mb-2', children: _jsx(Typography, { variant: 'title3', children: t('Group Assignment') }) }), _jsx(CheckboxInput, { checked: skipGroupAssignment, label: t("Don't assign to any group"), onChange: e => {
                                    setSkipGroupAssignment(e.currentTarget.checked);
                                    if (e.currentTarget.checked) {
                                        setSelectedAccreditationIds([]);
                                    }
                                }, additionalInfo: t('dont_assign_to_any_group_desc') }), !skipGroupAssignment && (_jsx("div", { className: 'mt-4', children: _jsx(AccreditationsPicker, { label: t('Select the Group/Groups the School should be assigned to'), accreditationIds: selectedAccreditationIds, onChange: setSelectedAccreditationIds, errors: errors.accreditations, clientId: approval.clientId, disablePortal: true }) }))] }))] })), action === 'adjust_amount' && approval.workflowType === WorkflowType.BUDGET_APPROVAL && (_jsx("div", { className: 'mb-5', children: _jsx(TextInput, { required: true, type: 'number', label: t('New approved amount'), value: newApprovedAmount?.toString() || '', onChange: e => setNewApprovedAmount(Number(e.target.value)), errors: errors.newApprovedAmount }) })), commentOptions.length > 1 && (_jsx(RadioButtons, { required: true, groupLabel: action === 'reject'
                    ? t('Reasons for denial')
                    : action === 'adjust_amount'
                        ? t('Reasons for adjusted amount')
                        : t('Comment type'), name: 'commentInternal', values: commentOptions, selectedValue: commentOption, onChange: e => setCommentOption(e.currentTarget.value) })), commentOptions.length === 1 && (_jsxs("div", { children: [_jsxs(Typography, { variant: 'label1', children: [action === 'reject'
                                ? t('Reasons for denial')
                                : action === 'adjust_amount'
                                    ? t('Reasons for adjusted amount')
                                    : t('Comment type'), "\u00A0*"] }), _jsx(Typography, { variant: 'body1', className: 'py-2', children: commentOptions[0].label })] })), commentOption === 'add' && (_jsx(RadioButtons, { required: true, groupLabel: t('Visibility'), name: 'displayCommentVisibility', values: [
                    {
                        label: t('Public (Visible to {employeeName})', {
                            values: { employeeName: approval.employeeName },
                        }),
                        value: 'false',
                    },
                    { label: t('Internal (Visible to approvers & admins)'), value: 'true' },
                ], selectedValue: displayCommentVisibility, onChange: e => setDisplayCommentVisibility(e.currentTarget.value) })), (action !== 'approve' || commentOption === 'add') && (_jsx(TextArea, { fullWidth: true, required: true, label: action === 'reject' || action === 'adjust_amount' ? t('Reasons') : t('Comment'), value: notes, onChange: e => setNotes(e.currentTarget.value), errors: errors.commentText }))] })), [
        action,
        messages,
        description,
        commentOptions,
        t,
        addToEmployerSchoolList,
        hasAccreditations,
        skipGroupAssignment,
        selectedAccreditationIds,
        errors,
        commentOption,
        displayCommentVisibility,
        notes,
        approval,
        newApprovedAmount,
    ]);
    const actionsContent = useMemo(() => (_jsxs(_Fragment, { children: [_jsx(TrackableButton, { enText: 'Cancel', tag: 'comment_modal', appearance: 'outline', onClick: onClose, disabled: saving, children: t('Cancel') }), _jsx(TrackableButton, { enText: 'Submit', tag: 'comment_modal', appearance: 'primary', onClick: handleSubmit, disabled: saving, children: getCtaText(t, action) })] })), [saving, action, onClose, handleSubmit, t]);
    return (_jsxs(_Fragment, { children: [!isModal && (_jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx(Typography, { variant: 'title3', children: title }), bodyContent, _jsx("div", { className: 'flex gap-3', children: actionsContent })] })), !showConfirm && isModal && (_jsxs(Dialog, { isOpen: true, title: title, onClose: onClose, fullScreen: true, children: [_jsx(DialogContent, { className: 'flex flex-col gap-2', children: bodyContent }), _jsx(DialogActions, { fullScreen: true, children: actionsContent })] })), showConfirm && (_jsx(Confirm, { title: getSuccessTitle(t, action), message: getSuccessMessage(t, action, approval.employeeName), onConfirm: async () => {
                    await refetchQueries();
                    onClose();
                    onComplete();
                }, onCancel: async () => {
                    await refetchQueries();
                    onClose();
                }, okText: t('OK'), cancelText: '' }))] }));
};
